/**
 * Build a Box V2 — Savor Theme
 *
 * Interactive logic for the build-a-box-v2 product section.
 * Extends Savor's Component base class for ref/event binding.
 *
 * Architecture:
 *   Single product with two options: Box Size (12, 24, 36) and
 *   Flavour (5 soup varieties) = 15 variants.
 *   The user picks a box size (which sets the pack count target),
 *   then selects flavour quantities. Each selected flavour is
 *   added to the cart as its own line item using the variant
 *   that matches the chosen size + flavour combination.
 *
 * Features:
 *   Step 1: Size selector cards (total box price = per-pack × pack count)
 *   Subscription toggle with selling plan integration
 *   Step 2: Flavour quantity picker with +/- controls
 *   Add to cart — multiple line items (one per flavour)
 */

import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

// Capture native fetch at module scope BEFORE Appstle monkey-patches it.
// Appstle's appstle-subscription.min.js monkey-patches window.fetch to
// intercept /cart/add requests and can strip selling_plan from the payload.
const nativeFetch = window.fetch.bind(window);

class BuildABoxV2Component extends Component {

  // ==================== SIZE TIERS ====================
  // Maps size index to target pack count
  static SIZE_TIERS = [
    { target: 12, sizeIndex: 0 },
    { target: 24, sizeIndex: 1 },
    { target: 36, sizeIndex: 2 },
  ];

  static getTargetForTier(tierIndex) {
    return BuildABoxV2Component.SIZE_TIERS[tierIndex]?.target ?? 12;
  }

  // ==================== STATE ====================

  /** @type {number} */
  selectedSizeIndex = 1;

  /** @type {boolean} */
  subscriptionEnabled = false;

  /** @type {number|null} */
  selectedSellingPlanId = null;

  /** @type {Object<string, number>} */
  flavourSelections = {};

  /** @type {boolean} */
  isProcessing = false;

  /** @type {number} */
  _userSelectedSizeIndex = 1;

  /** @type {boolean} */
  _prevAtcEnabled = false;

  // ==================== LIFECYCLE ====================

  connectedCallback() {
    super.connectedCallback();

    // Parse embedded product data
    const dataEl = this.querySelector('[ref="productData"]');
    if (!dataEl) return;
    this.productData = JSON.parse(dataEl.textContent);

    // Read attributes
    this.sectionId = this.dataset.sectionId || '';
    this.hasSelling = this.dataset.hasSellingPlans === 'true';

    // Data model
    this.sizes = this.productData.sizes || [];
    this.flavours = this.productData.flavours || [];
    this.allVariants = this.productData.variants || [];
    this.sellingPlanGroups = this.productData.selling_plan_groups || [];

    // Initialise flavour selections to 0
    this.flavours.forEach(f => { this.flavourSelections[f.name] = 0; });

    // Set up selling plans
    if (this.hasSelling) {
      this.#extractSellingPlans();
      this.#populateFrequencyDropdown();
      this.#updateSubscriptionPercentLabel();
    }

    // Initial UI state
    this.#updateUI();
  }

  // ==================== PRICE FORMATTING ====================

  /**
   * @param {number} amountInSubunits - Price in subunits (e.g. pence)
   * @returns {string}
   */
  #formatPrice(amountInSubunits) {
    const amount = amountInSubunits / 100;
    try {
      const currencyCode = window.Shopify?.currency?.active || 'GBP';
      const locale = document.documentElement.lang || 'en';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch {
      return `£${amount.toFixed(2)}`;
    }
  }

  // ==================== STEP 1: SIZE SELECTION ====================

  /**
   * @param {MouseEvent} event
   */
  handleSizeSelect(event) {
    const card = /** @type {HTMLElement} */ (event.target).closest('[data-size-index]');
    if (!card) return;

    const newIndex = parseInt(/** @type {HTMLElement} */ (card).dataset.sizeIndex, 10);
    if (newIndex < 0 || newIndex >= this.sizes.length) return;

    this.selectedSizeIndex = newIndex;
    this._userSelectedSizeIndex = newIndex;

    // Toggle active class and ARIA
    const sizeCards = this.querySelectorAll('[data-size-index]');
    sizeCards.forEach(c => {
      c.classList.remove('bab-size-card--active');
      c.setAttribute('aria-checked', 'false');
    });
    card.classList.add('bab-size-card--active');
    card.setAttribute('aria-checked', 'true');

    // Update all prices on cards if subscription is active
    if (this.subscriptionEnabled) {
      this.#updateAllCardPrices();
    }

    this.#updateUI();
  }

  // ==================== SUBSCRIPTION TOGGLE ====================

  handleSubscriptionToggle() {
    const toggle = this.querySelector('[data-subscription-toggle]');
    if (!toggle) return;

    this.subscriptionEnabled = /** @type {HTMLInputElement} */ (toggle).checked;

    // Animated expand/collapse subscription details
    const detailsEl = this.querySelector('[data-subscription-details]');
    if (detailsEl) {
      detailsEl.classList.toggle('bab-subscription-details--open', this.subscriptionEnabled);
      detailsEl.setAttribute('aria-hidden', String(!this.subscriptionEnabled));
    }

    // Update aria-expanded on header
    const headerEl = this.querySelector('[data-subscription-header]');
    if (headerEl) {
      headerEl.setAttribute('aria-expanded', String(this.subscriptionEnabled));
    }

    // Toggle active style on subscription card
    const subCard = this.querySelector('[data-subscription-section]');
    if (subCard) {
      subCard.classList.toggle('bab-subscription-card--active', this.subscriptionEnabled);
    }

    // Update displayed prices on all cards
    this.#updateAllCardPrices();
    this.#updateUI();
  }

  /**
   * @param {MouseEvent} event
   */
  handleSubscriptionHeaderClick(event) {
    // Skip if click is on the toggle label itself
    if (/** @type {HTMLElement} */ (event.target).closest('.bab-toggle-label')) return;

    const toggle = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-subscription-toggle]'));
    if (toggle) {
      toggle.checked = !toggle.checked;
      this.handleSubscriptionToggle();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleSubscriptionHeaderKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const toggle = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-subscription-toggle]'));
      if (toggle) {
        toggle.checked = !toggle.checked;
        this.handleSubscriptionToggle();
      }
    }
  }

  handleFrequencyChange() {
    const select = /** @type {HTMLSelectElement|null} */ (this.querySelector('[data-frequency-select]'));
    if (!select) return;

    this.selectedSellingPlanId = parseInt(select.value, 10);
    this.#updateAllCardPrices();
    this.#updateSubscriptionPercentLabel();
  }

  #extractSellingPlans() {
    this.sellingPlans = [];
    for (const group of this.sellingPlanGroups) {
      for (const plan of group.selling_plans) {
        this.sellingPlans.push(plan);
      }
    }
  }

  #populateFrequencyDropdown() {
    const select = this.querySelector('[data-frequency-select]');
    if (!select || this.sellingPlans.length === 0) return;

    select.innerHTML = '';
    this.sellingPlans.forEach((plan, i) => {
      const option = document.createElement('option');
      option.value = plan.id;
      option.textContent = plan.name;
      if (i === 0) option.selected = true;
      select.appendChild(option);
    });

    this.selectedSellingPlanId = this.sellingPlans[0].id;
  }

  #updateSubscriptionPercentLabel() {
    const percentEl = this.querySelector('[data-subscription-percent]');
    if (!percentEl) return;
    const plan = this.#getSelectedSellingPlan();
    if (plan?.price_adjustments?.length > 0) {
      const adj = plan.price_adjustments[0];
      if (adj.value_type === 'percentage') {
        percentEl.textContent = adj.value;
      }
    }
  }

  #getSelectedSellingPlan() {
    if (!this.selectedSellingPlanId) return null;
    return this.sellingPlans.find(p => p.id === this.selectedSellingPlanId) || null;
  }

  /**
   * Calculate the subscription-discounted price.
   * @param {number} priceInCents
   * @returns {number}
   */
  #getSubscriptionPrice(priceInCents) {
    const plan = this.#getSelectedSellingPlan();
    if (!plan?.price_adjustments?.length) return priceInCents;

    const adj = plan.price_adjustments[0];
    if (adj.value_type === 'percentage') {
      return Math.round(priceInCents * (100 - adj.value) / 100);
    } else if (adj.value_type === 'fixed_amount') {
      return Math.max(0, priceInCents - adj.value);
    }
    return priceInCents;
  }

  // ==================== STEP 2: FLAVOUR PICKER ====================

  /**
   * @param {MouseEvent} event
   */
  handleFlavourPlus(event) {
    const target = /** @type {HTMLElement} */ (event.target);

    // Clicking the minus button or qty value should not increment
    if (target.closest('[data-flavour-minus]') || target.closest('[data-flavour-qty]')) return;

    const card = /** @type {HTMLElement|null} */ (target.closest('[data-flavour-name]'));
    if (!card) return;
    const name = card.dataset.flavourName;

    // Cap at the currently selected size's target
    const sizeTarget = BuildABoxV2Component.getTargetForTier(this.selectedSizeIndex);
    if (this.#getTotalSelected() >= sizeTarget) return;

    this.flavourSelections[name] = (this.flavourSelections[name] || 0) + 1;
    this.#updateFlavourCardDisplay(card, name);
    this.#triggerTapAnimation(card);
    this.#updateUI();
  }

  /**
   * @param {MouseEvent} event
   */
  handleFlavourMinus(event) {
    const btn = /** @type {HTMLElement} */ (event.target).closest('[data-flavour-minus]');
    if (!btn) return;

    const card = /** @type {HTMLElement|null} */ (btn.closest('[data-flavour-name]'));
    if (!card) return;
    const name = card.dataset.flavourName;

    if ((this.flavourSelections[name] || 0) <= 0) return;

    this.flavourSelections[name] -= 1;
    this.#updateFlavourCardDisplay(card, name);
    this.#triggerTapAnimation(card);
    this.#updateUI();
  }

  /**
   * @param {HTMLElement} card
   */
  #triggerTapAnimation(card) {
    card.classList.remove('bab-flavour-card--tap');
    void card.offsetWidth;
    card.classList.add('bab-flavour-card--tap');
    card.addEventListener('animationend', () => {
      card.classList.remove('bab-flavour-card--tap');
    }, { once: true });
  }

  /**
   * @param {HTMLElement} card
   * @param {string} name
   */
  #updateFlavourCardDisplay(card, name) {
    const qtyEl = card.querySelector('[data-flavour-qty]');
    if (qtyEl) qtyEl.textContent = String(this.flavourSelections[name] || 0);

    card.classList.toggle('bab-flavour-card--has-qty', (this.flavourSelections[name] || 0) > 0);

    const minusBtn = /** @type {HTMLButtonElement|null} */ (card.querySelector('[data-flavour-minus]'));
    if (minusBtn) minusBtn.disabled = (this.flavourSelections[name] || 0) <= 0;
  }

  // ==================== UI UPDATE ====================

  #updateUI() {
    const totalSelected = this.#getTotalSelected();
    const boxCount = BuildABoxV2Component.getTargetForTier(this.selectedSizeIndex);
    const isComplete = (totalSelected === boxCount);
    const isOver = totalSelected > boxCount;

    // Update progress bar
    const progressFill = this.querySelector('[data-progress-fill]');
    const progressBar = this.querySelector('[data-progress-bar]');

    if (progressFill) {
      const pct = Math.min(100, (totalSelected / boxCount) * 100);
      /** @type {HTMLElement} */ (progressFill).style.width = pct + '%';
      progressFill.classList.toggle('bab-progress-bar__fill--complete', isComplete);
      progressFill.classList.toggle('bab-progress-bar__fill--over', isOver);

      if (isComplete && !progressFill.classList.contains('bab-progress-bar__fill--pulse')) {
        progressFill.classList.add('bab-progress-bar__fill--pulse');
        progressFill.addEventListener('animationend', () => {
          progressFill.classList.remove('bab-progress-bar__fill--pulse');
        }, { once: true });
      }
    }

    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', String(totalSelected));
      progressBar.setAttribute('aria-valuemax', String(boxCount));
    }

    // Toggle step 2 complete class
    const step2El = this.querySelector('[data-step="2"]');
    if (step2El) {
      step2El.classList.toggle('bab-step--complete', isComplete);
    }

    // ATC button state
    const addToCartBtn = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-add-to-cart]'));
    const addToCartText = this.querySelector('[data-add-to-cart-text]');
    const atcSubtotalWrap = this.querySelector('[data-atc-subtotal-wrap]');

    const prevEnabled = this._prevAtcEnabled;
    if (addToCartBtn) {
      addToCartBtn.disabled = !isComplete;
      addToCartBtn.classList.toggle('bab-atc--over', isOver);

      if (isComplete && !prevEnabled) {
        addToCartBtn.classList.add('bab-atc--ready');
        addToCartBtn.addEventListener('animationend', () => {
          addToCartBtn.classList.remove('bab-atc--ready');
        }, { once: true });
      }
    }
    this._prevAtcEnabled = isComplete;

    // Dynamic button text
    if (addToCartText) {
      if (isOver) {
        const excess = totalSelected - boxCount;
        addToCartText.textContent = `REMOVE ${excess} PACK${excess !== 1 ? 'S' : ''}`;
      } else if (isComplete) {
        addToCartText.textContent = this.dataset.atcText || 'ADD TO CART';
      } else {
        addToCartText.textContent = `${totalSelected} of ${boxCount} packs selected`;
      }
    }

    // Show subtotal suffix only when complete
    if (atcSubtotalWrap) {
      atcSubtotalWrap.classList.toggle('bab-hidden', !isComplete || isOver);
    }

    // Disable + buttons when at target
    const disablePlus = totalSelected >= boxCount;
    const flavourCards = this.querySelectorAll('[data-flavour-name]');
    flavourCards.forEach(card => {
      const name = /** @type {HTMLElement} */ (card).dataset.flavourName;
      const minusBtn = /** @type {HTMLButtonElement|null} */ (card.querySelector('[data-flavour-minus]'));
      const plusBtn = /** @type {HTMLButtonElement|null} */ (card.querySelector('[data-flavour-plus]'));
      if (minusBtn) minusBtn.disabled = (this.flavourSelections[name] || 0) <= 0;
      if (plusBtn) plusBtn.disabled = disablePlus;
    });

    // Update subtotal
    this.#updateSubtotal();
  }

  // ==================== PRICE DISPLAY ====================

  #updateAllCardPrices() {
    const sizeCards = this.querySelectorAll('[data-size-index]');
    sizeCards.forEach((card, idx) => {
      const size = this.sizes[idx];
      if (!size) return;

      const boxCount = size.box_count || 1;
      const priceEl = card.querySelector('[data-variant-price]');
      const unitPriceEl = card.querySelector('[data-unit-price]');
      const comparePriceEl = card.querySelector('[data-compare-price]');
      const badgeEl = card.querySelector('[data-discount-badge]');

      let perBagPrice = size.price;
      let perBagCompare = size.compare_at_price;

      if (this.subscriptionEnabled) {
        perBagPrice = this.#getSubscriptionPrice(size.price);
        if (!perBagCompare || perBagCompare <= 0) {
          perBagCompare = size.price;
        }
      }

      const totalPrice = perBagPrice * boxCount;
      const totalCompare = perBagCompare ? perBagCompare * boxCount : 0;

      if (priceEl) {
        priceEl.textContent = this.#formatPrice(totalPrice);
        if (this.subscriptionEnabled && perBagPrice < size.price) {
          priceEl.classList.add('bab-size-card__sub-price');
          priceEl.classList.remove('bab-size-card__price');
        } else {
          priceEl.classList.remove('bab-size-card__sub-price');
          priceEl.classList.add('bab-size-card__price');
        }
      }

      if (unitPriceEl) {
        const servings = size.servings || boxCount;
        unitPriceEl.textContent = this.#formatPrice(Math.round(totalPrice / servings)) + '/pack';
      }

      if (comparePriceEl) {
        if (totalCompare > totalPrice) {
          comparePriceEl.textContent = this.#formatPrice(totalCompare);
          /** @type {HTMLElement} */ (comparePriceEl).style.display = '';
        } else {
          /** @type {HTMLElement} */ (comparePriceEl).style.display = 'none';
        }
      }

      if (badgeEl) {
        if (!this.subscriptionEnabled && totalCompare > totalPrice) {
          const discountPct = Math.round((totalCompare - totalPrice) / totalCompare * 100);
          badgeEl.textContent = `${discountPct}% OFF`;
          /** @type {HTMLElement} */ (badgeEl).style.visibility = '';
        } else {
          /** @type {HTMLElement} */ (badgeEl).style.visibility = 'hidden';
        }
      }
    });
  }

  // ==================== SUBTOTAL ====================

  #updateSubtotal() {
    const size = this.sizes[this.selectedSizeIndex] || this.sizes[0];
    if (!size) return;

    const totalQty = this.#getTotalSelected();
    let perBagPrice = size.price;
    if (this.subscriptionEnabled) {
      perBagPrice = this.#getSubscriptionPrice(size.price);
    }

    const subtotal = perBagPrice * totalQty;
    const subtotalEl = this.querySelector('[data-subtotal-amount]');
    if (subtotalEl) {
      subtotalEl.textContent = totalQty > 0 ? this.#formatPrice(subtotal) : this.#formatPrice(0);
    }
  }

  // ==================== HELPERS ====================

  /**
   * Look up the variant ID for a given flavour at the currently selected size.
   * @param {string} flavourName
   * @returns {number|null}
   */
  #getVariantIdForFlavour(flavourName) {
    const currentSize = this.sizes[this.selectedSizeIndex]?.name;
    if (!currentSize) return null;
    const variant = this.allVariants.find(v => v.size === currentSize && v.flavour === flavourName);
    return variant ? variant.id : null;
  }

  /** @returns {number} */
  #getTotalSelected() {
    return Object.values(this.flavourSelections).reduce((sum, qty) => sum + qty, 0);
  }

  // ==================== CART SUBMISSION ====================

  async handleAddToCart() {
    const totalSelected = this.#getTotalSelected();
    const boxCount = BuildABoxV2Component.getTargetForTier(this.selectedSizeIndex);
    if (totalSelected !== boxCount) return;
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.#showLoadingState();

    // Build line items — one per selected flavour
    const items = [];
    for (const [flavourName, qty] of Object.entries(this.flavourSelections)) {
      if (qty <= 0) continue;

      const variantId = this.#getVariantIdForFlavour(flavourName);
      if (!variantId) continue;

      /** @type {{id: number, quantity: number, properties: Record<string, string>, selling_plan?: number}} */
      const item = { id: variantId, quantity: qty, properties: { _build_a_box: 'true' } };

      if (this.subscriptionEnabled && this.selectedSellingPlanId) {
        item.selling_plan = this.selectedSellingPlanId;
      }

      items.push(item);
    }

    if (items.length === 0) {
      this.#hideLoadingState();
      this.isProcessing = false;
      return;
    }

    try {
      const response = await nativeFetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (data.status && data.status !== 200) {
        console.error('Build a Box — cart error:', data.description || data.message);
        const addToCartText = this.querySelector('[data-add-to-cart-text]');
        if (addToCartText) {
          addToCartText.textContent = data.message || 'Error — try again';
        }
        this.#hideLoadingState();
        this.isProcessing = false;
        return;
      }

      // Dispatch CartAddEvent — cart drawer auto-opens, cart icon updates
      this.dispatchEvent(
        new CartAddEvent({}, this.sectionId, {
          source: 'build-a-box-v2',
          itemCount: totalSelected,
        })
      );

      // Show success state
      const addToCartText = this.querySelector('[data-add-to-cart-text]');
      if (addToCartText) addToCartText.textContent = 'ADDED!';
      this.#hideLoadingState();

      setTimeout(() => {
        this.isProcessing = false;
        this.#updateUI();
      }, 2000);

    } catch (error) {
      console.error('Build a Box — cart add error:', error);
      const addToCartText = this.querySelector('[data-add-to-cart-text]');
      if (addToCartText) addToCartText.textContent = 'Error — try again';
      this.#hideLoadingState();
      this.isProcessing = false;
    }
  }

  // ==================== LOADING STATE ====================

  #showLoadingState() {
    const addToCartText = this.querySelector('[data-add-to-cart-text]');
    const spinner = this.querySelector('[data-loading-spinner]');
    const btn = this.querySelector('[data-add-to-cart]');
    if (addToCartText) /** @type {HTMLElement} */ (addToCartText).style.display = 'none';
    if (spinner) spinner.classList.remove('bab-hidden');
    if (btn) /** @type {HTMLButtonElement} */ (btn).disabled = true;
  }

  #hideLoadingState() {
    const spinner = this.querySelector('[data-loading-spinner]');
    const addToCartText = this.querySelector('[data-add-to-cart-text]');
    if (spinner) spinner.classList.add('bab-hidden');
    if (addToCartText) /** @type {HTMLElement} */ (addToCartText).style.display = '';
  }
}

if (!customElements.get('build-a-box-v2-component')) {
  customElements.define('build-a-box-v2-component', BuildABoxV2Component);
}
